import { API_BASE_URL } from '../config/api';
import { adminRequest, clearStoredAuthTokens, setStoredAuthTokens } from './http';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
}

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
}

export type User = AuthUser;

class AuthService {
  async login(email: string, password: string): Promise<LoginResponse> {
    // 1. 先调用登录接口获取 token
    const loginResponse = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'zh-CN',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!loginResponse.ok) {
      const errorData = await loginResponse.json().catch(() => ({}));
      throw new Error(errorData.message || '登录失败，请检查邮箱和密码');
    }

    const loginData = await loginResponse.json();
    const { session } = loginData.data;

    if (!session?.access_token || !session?.refresh_token) {
      throw new Error('登录失败：未获取到访问令牌');
    }

    // 2. 使用获取到的 token 调用 /me 接口获取我们数据库中的用户信息
    const userResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'Accept-Language': 'zh-CN',
      },
    });

    if (!userResponse.ok) {
      const errorData = await userResponse.json().catch(() => ({}));
      throw new Error(errorData.message || '获取用户信息失败');
    }

    const userData = await userResponse.json();
    const user = userData.data;

    // 3. 检查管理员权限
    if (!user.isAdmin) {
      throw new Error('您没有管理员权限');
    }

    setStoredAuthTokens(session.access_token, session.refresh_token);

    return {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      user: user,
    };
  }

  async getCurrentUser(): Promise<AuthUser> {
    return adminRequest<AuthUser>('/api/auth/me');
  }

  async logout(): Promise<void> {
    try {
      await adminRequest('/api/auth/logout', { method: 'POST' }, { retryOnAuthError: false });
    } catch (error) {
      // 即使logout失败也要清除本地token
      console.warn('Logout request failed:', error);
    } finally {
      clearStoredAuthTokens();
    }
  }
}

export const authService = new AuthService();
